import { FormEventHandler, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import {
    MANAGEMENT_USER_LICENSE_ISSUE,
    MANAGEMENT_USER_LICENSE_UPDATE,
    MANAGEMENT_USER_SEARCH,
    MYPAGE_ROUTE,
} from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { User } from "../types/User";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import { License } from "../types/License";
import { getLicenses, putLicense } from "../clients/LicenseClient";
import { formatUtcToLocalDateTime } from "../utils/DateUtils";
import LicenseDetailModal from "./modals/LicenseDetailModal";
import LicenseEditModal from "./modals/LicenseEditModal";
import PagenationButtons from "../components/PagenationButtons";

interface LocationState {
    selectedUser: User;
}

/**
 * 管理者向け - ライセンス使用状況確認画面
 * @returns JSX.Element
 */
const ManagementUserLicenseSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getRecentToken } = useUserContext();
    const location = useLocation();
    const { selectedUser } = location.state as LocationState;
    const [selectedLicense, setSelectedLicense] = useState<License | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>("");
    const [licenseList, setLicenseList] = useState<License[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string>("");

    // ページネーション
    const [licensePerPage, setLicensePerPage] = useState(10);
    const [licenceCurrentPage, setLicenseCurrentPage] = useState(1);
    const [licenseTotalPages, setLicenseTotalPages] = useState(1);

    // ライセンス情報詳細モーダル関連
    const [showLicenseDetailModal, setShowLicenseDetailModal] = useState<boolean>(false);
    const [errorMessageForLicenseDetail, setErrorMessageForLicenseDetail] = useState<string>("");

    // ライセンス情報編集モーダル関連
    const [showLicenseEditModal, setShowLicenseEditModal] = useState<boolean>(false);
    const [errorMessageForLicenseEdit, setErrorMessageForLicenseEdit] = useState<string>("");

    /**
     * 処理中フラグ
     *
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);

    // ページ初期化処理（DBから情報の取得）
    useEffect(() => {
        if (!selectedUser) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        if (!effectRun.current) {
            fetchLicenses();
            return () => {
                effectRun.current = true;
            };
        }
    }, [selectedUser]);

    /**
     * 検索押下時のイベント処理
     * @param event
     */
    const handleSearchUser: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        setIsProcessing(true);
        try {
            // 現在は使用を想定していない
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
        } finally {
            setIsProcessing(false);
        }
    };

    /**
     * ライセンス情報一覧の取得処理
     * @returns
     */
    async function fetchLicenses() {
        setLoading(true);
        setErrorMessage("");
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]ライセンス情報一覧の取得
            const licenseListResponse: License[] = await getLicenses(idToken, {
                user_id: selectedUser.user_id,
            });
            // 登録日時順でソート
            const sortedLicenseList: License[] = licenseListResponse.sort((a, b) => {
                return new Date(b.insert_date).getTime() - new Date(a.insert_date).getTime();
            });
            setLicenseList(sortedLicenseList);
            // ページネーション用設定
            setLicenseTotalPages(Math.ceil(sortedLicenseList.length / licensePerPage));
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setLoading(false);
        }
    }

    /**
     * ライセンス情報更新処理
     * @param license 選択したライセンス情報
     */
    const updateLicense = async (lisence: License, request: { [name: string]: any }) => {
        setErrorMessageForLicenseEdit("");
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessageForLicenseEdit(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]ライセンス情報更新
            await putLicense(idToken, lisence.license_id, request);
        } catch (error: any) {
            setErrorMessageForLicenseEdit("ライセンス情報の更新に失敗しました。");
            return false;
        } finally {
        }
        return true;
    };

    /**
     * [ライセンス詳細情報確認モーダル]
     * ライセンス詳細選択時のイベント処理
     * @param license 選択したライセンス情報
     */
    const handleLicenseDetail = (license: License) => {
        setSelectedLicense(license);
        setErrorMessageForLicenseDetail("");
        setShowLicenseDetailModal(true);
    };

    /**
     * [ライセンス詳細情報確認モーダル]
     * キャンセル押下時のイベント処理
     */
    const handleCloseLicenseDetailModal = () => {
        setSelectedLicense(undefined);
        setShowLicenseDetailModal(false);
    };

    /**
     * [ライセンス情報編集モーダル]
     * ライセンス情報編集時のイベント処理
     * @param license 選択したライセンス情報
     */
    const handleLicenseEdit = (license: License) => {
        setSelectedLicense(license);
        setErrorMessageForLicenseEdit("");
        setShowLicenseEditModal(true);
    };

    /**
     * [ライセンス情報編集モーダル]
     * キャンセル押下時のイベント処理
     */
    const handleCloseLicenseEditModal = () => {
        setSelectedLicense(undefined);
        setShowLicenseEditModal(false);
    };

    /**
     * [ライセンス情報編集モーダル]
     * 閉じる押下時のイベント処理
     */
    const handleCloseLicenseEditModalAndRefresh = () => {
        setSelectedLicense(undefined);
        setShowLicenseEditModal(false);
        fetchLicenses();
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title="ライセンス使用状況確認 | DF WebService" />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">ライセンス使用状況確認</h1>
                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}
                <form className="mx-auto max-w-2xl" onSubmit={handleSearchUser}>
                    <div className="grid grid-cols-3 items-center justify-center gap-6 pb-6 pt-2 sm:m-2">
                        {/* 検索文字列 */}
                        <div className="col-span-2 py-2">
                            <input
                                type="text"
                                id="searchText"
                                placeholder="メールアドレス"
                                className="textbox-common text-gray-400"
                                onChange={(e) => setSearchText(e.target.value)}
                                maxLength={50}
                                required
                                defaultValue={selectedUser.email}
                                autoComplete="off"
                                disabled
                            />
                        </div>
                        {/* 検索ボタン（画面遷移時に自動で発火するため使用想定しない） */}
                        <div className="col-span-1">
                            <button
                                type="submit"
                                className="btn-primary-disabled w-[100px] bg-gray-300"
                                disabled={true}
                            >
                                {t("CommonLabel.search")}
                            </button>
                        </div>
                    </div>
                </form>
                <hr className="my-5 border-b border-t-2" />
                {loading ? (
                    // ローディング
                    <div className="flex flex-col items-center justify-center p-10">
                        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-lime-800"></div>
                        <p className="p-2 font-bold text-lime-800">Loading...</p>
                    </div>
                ) : licenseList.length === 0 ? (
                    // ライセンスが存在しない場合
                    <>
                        <div className="flex items-center justify-center py-4 font-bold text-gray-700">
                            「{selectedUser.email}」に紐づいているライセンスはありません。
                        </div>
                        <div className="mx-auto flex items-center justify-evenly py-3">
                            <Link
                                className="inline-flex items-center gap-x-1.5 text-sm font-medium text-blue-700 hover:underline"
                                to={MANAGEMENT_USER_LICENSE_ISSUE}
                                state={{ selectedUser: selectedUser }}
                            >
                                <button type="button" onClick={() => {}} className="btn-primary mx-2 w-[300px]">
                                    新規にライセンスを発行
                                </button>
                            </Link>
                        </div>
                    </>
                ) : (
                    // ライセンスが見つかった場合
                    <div className="mx-auto max-w-6xl overflow-x-auto">
                        <div className="flex items-center justify-center font-bold text-gray-700">
                            「{selectedUser.email}」は{licenseList.length}件のライセンスを保有しています。
                        </div>
                        <table className="my-3 min-w-full divide-y divide-gray-200 border">
                            <thead className="bg-gray-400">
                                <tr
                                    className={`${
                                        i18n.language === "ja" ? "text-base" : "text-sm"
                                    } font-semibold uppercase tracking-wide text-gray-100`}
                                >
                                    {/* [ヘッダ] 製品名 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.productName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] ライセンスタイプ */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.licenseType")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 利用開始日 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.activationDate")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 有効期限 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.expirationDate")}</span>
                                        </div>
                                    </th>
                                    {/* ライセンス詳細 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left"></th>
                                    {/* 有効期間を編集 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left"></th>
                                    {/* ライセンス更新 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {licenseList
                                    .slice(
                                        (licenceCurrentPage - 1) * licensePerPage,
                                        (licenceCurrentPage - 1) * licensePerPage + licensePerPage,
                                    )
                                    .map((l) => (
                                        <tr key={l.license_id} className="text-sm font-semibold text-gray-800">
                                            {/* 製品名 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-6 py-2">
                                                    <span>{l.package_name}</span>
                                                </div>
                                            </td>
                                            {/* ライセンスタイプ */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-6 py-2">
                                                    <span>
                                                        {l.free_trial_flg
                                                            ? t("CommonLabel.freeTrialVersion")
                                                            : l.permanent_flg
                                                            ? t("CommonLabel.permanent")
                                                            : t("CommonLabel.subscribe")}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* 利用開始日時 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-6 py-2">
                                                    <span>{formatUtcToLocalDateTime(l.activation_date)}</span>
                                                </div>
                                            </td>
                                            {/* 有効期限日時 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-6 py-2">
                                                    <span>
                                                        {l.permanent_flg
                                                            ? "-"
                                                            : formatUtcToLocalDateTime(l.expire_date)}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* 詳細を確認 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-2 py-1.5">
                                                    <button
                                                        className="text-sm font-medium text-blue-700 hover:underline"
                                                        onClick={() => handleLicenseDetail(l)}
                                                    >
                                                        詳細を確認
                                                    </button>
                                                </div>
                                            </td>
                                            {/* 期間を編集 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-2 py-1.5">
                                                    <button
                                                        className="text-sm font-medium text-blue-700 hover:underline"
                                                        onClick={() => handleLicenseEdit(l)}
                                                    >
                                                        有効期間編集
                                                    </button>
                                                </div>
                                            </td>
                                            {/* ライセンスを更新 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <Link
                                                    className="px-2 py-1.5"
                                                    to={MANAGEMENT_USER_LICENSE_UPDATE}
                                                    state={{
                                                        selectedUser: selectedUser,
                                                        selectedLicense: l,
                                                    }}
                                                >
                                                    <button
                                                        className={`${
                                                            l.permanent_flg
                                                                ? "text-gray-300"
                                                                : "text-blue-700 hover:underline"
                                                        } text-sm font-medium `}
                                                        onClick={() => {}}
                                                        disabled={l.permanent_flg}
                                                    >
                                                        ライセンス更新
                                                    </button>
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        {/* ページネーションコントロール */}
                        <PagenationButtons
                            currentPage={licenceCurrentPage}
                            setCurrentPage={setLicenseCurrentPage}
                            totalPages={licenseTotalPages}
                        />
                        <div className="mx-auto flex items-center justify-evenly pt-8">
                            <Link
                                className="inline-flex items-center gap-x-1.5 text-sm font-medium text-blue-700 hover:underline"
                                to={MANAGEMENT_USER_LICENSE_ISSUE}
                                state={{ selectedUser: selectedUser }}
                            >
                                <button type="button" onClick={() => {}} className="btn-primary mx-2 w-[300px]">
                                    新規にライセンスを発行
                                </button>
                            </Link>
                        </div>
                    </div>
                )}

                {/* DFアカウント検索へ戻る */}
                <div className="flex items-center justify-center">
                    <div className="grid max-w-md grid-cols-2 gap-6 py-6 sm:m-2">
                        {/* DFアカウント検索へ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() => navigate(MANAGEMENT_USER_SEARCH)}
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                DFアカウント検索へ戻る
                            </button>
                        </div>
                        {/* マイメニューへ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() => navigate(MYPAGE_ROUTE)}
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                マイメニューへ戻る
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* ライセンス詳細情報確認モーダル */}
            <LicenseDetailModal
                showModal={showLicenseDetailModal}
                selectedLicense={selectedLicense}
                handleClose={handleCloseLicenseDetailModal}
                errorMessage={errorMessageForLicenseDetail}
            />
            {/* ライセンス情報編集モーダル */}
            <LicenseEditModal
                showModal={showLicenseEditModal}
                selectedLicense={selectedLicense}
                handleClose={handleCloseLicenseEditModal}
                handleCloseAndRefresh={handleCloseLicenseEditModalAndRefresh}
                updateLicense={updateLicense}
                errorMessage={errorMessageForLicenseEdit}
            />
            <Footer />
        </div>
    );
};
export default ManagementUserLicenseSearch;
